import { breakpoints } from '@galapagos/design-system/breakpoints';
import { colors } from '@galapagos/design-system/colors';
import { radius } from '@galapagos/design-system/radius';
import { sizing } from '@galapagos/design-system/sizing';
import { spacing } from '@galapagos/design-system/spacing';
import { typography } from '@galapagos/design-system/typography';
import { REWARDS_DOCK_HEIGHT_VAR } from './rewards-carousel-style';

type TypographyToken = keyof typeof typography;

export const SIGNUP_STEP_STYLE_ID = 'signup-step-style';

/**
 * Pulls a block out to both viewport edges from a column centred on the page,
 * whatever the column's width; the matching padding puts its contents back
 * on the column.
 */
const FULL_BLEED_MARGIN = 'calc(50% - 50vw)';
const FULL_BLEED_PADDING = 'calc(50vw - 50%)';

function font(token: TypographyToken): string {
  const t = typography[token];
  return `font-family: ${t.fontFamily}; font-weight: ${t.fontWeight}; font-size: ${t.fontSize}px; line-height: ${t.lineHeight}px; letter-spacing: ${t.letterSpacing}px;`;
}

/**
 * The step-body pieces from the v7 web sheet that have no design-system
 * component (Web/Steps list 69:222, Web/Privacy note 49:5639, Web/Form actions
 * 48:5469), as one stylesheet so the form-actions bar can switch layout at the
 * page-column breakpoint. On phones the bar sticks to the bottom edge, or on
 * top of the rewards card's dock while one shows, and runs edge to edge while
 * its buttons stay on the content column. It sits right under the content on
 * a short step and draws no line above its buttons. Its top padding is a fade
 * from transparent into the page surface (the design-system edge-fade colours),
 * so content scrolling under it dissolves rather than being cut off, and the
 * buttons always sit on solid surface. Taps on the faded strip reach the
 * content showing through it; only the buttons themselves catch taps. Its bottom padding is the safe-area
 * inset or a small margin, whichever is larger, never both stacked.
 * Every value is a design-system token. The steps list discs use the palette its Figma note
 * names (moss green 500, punch pink 200, cerulean 200).
 */
export function signupStepCss(): string {
  const bp = breakpoints.column;
  return `
.signup-step { display: flex; flex-direction: column; gap: ${spacing.m}px; flex: 1; }
.signup-step__group { display: flex; flex-direction: column; gap: ${spacing.m}px; }
.signup-steps-list { list-style: none; margin: 0; padding: ${spacing.s}px ${spacing.lg}px; background: ${colors.background.container}; border: ${sizing.stroke.hairline}px solid ${colors.stroke.divider}; border-radius: ${radius.xl}px; }
.signup-steps-list__item { display: flex; align-items: center; gap: ${spacing.m}px; padding: ${spacing.m}px 0; color: ${colors.text.primary}; ${font('b2Regular')} }
.signup-steps-list__item[aria-current='step'] { font-weight: ${typography.b2Bold.fontWeight}; }
.signup-steps-list__disc { display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0; width: ${sizing.icon.md}px; height: ${sizing.icon.md}px; border-radius: ${radius.rounded}px; ${font('b3Bold')} }
.signup-steps-list__item:nth-child(3n + 1) .signup-steps-list__disc { background: ${colors.mossGreen['500']}; }
.signup-steps-list__item:nth-child(3n + 2) .signup-steps-list__disc { background: ${colors.punchPink['200']}; }
.signup-steps-list__item:nth-child(3n) .signup-steps-list__disc { background: ${colors.cerulean['200']}; }
.signup-privacy-note { display: flex; align-items: center; justify-content: center; gap: ${spacing.s}px; margin: 0; color: ${colors.text.secondary}; ${font('c1Medium')} }
.signup-selected-note { margin: 0; color: ${colors.text.secondary}; ${font('c2Regular')} }
.signup-form-actions { position: sticky; bottom: var(${REWARDS_DOCK_HEIGHT_VAR}, 0px); margin-top: 0; margin-left: ${FULL_BLEED_MARGIN}; margin-right: ${FULL_BLEED_MARGIN}; display: flex; flex-direction: column; gap: ${spacing.s}px; align-items: center; padding: ${spacing.xl}px ${FULL_BLEED_PADDING} max(${spacing.m}px, env(safe-area-inset-bottom)); background: linear-gradient(to bottom, ${colors.nav.gradientStart} 0, ${colors.background.container} ${spacing.xl}px); pointer-events: none; }
.signup-form-actions > * { pointer-events: auto; }
@media (min-width: ${bp}px) {
  .signup-form-actions { position: static; margin-top: ${spacing.s}px; margin-left: 0; margin-right: 0; padding: 0 0 ${spacing.lg}px; background: none; pointer-events: auto; }
}
`;
}

/** Injects the step stylesheet once per document. No-op during prerender. */
export function ensureSignupStepStyle(): void {
  if (typeof document === 'undefined' || document.getElementById(SIGNUP_STEP_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = SIGNUP_STEP_STYLE_ID;
  style.textContent = signupStepCss();
  document.head.appendChild(style);
}
